import React from 'react';
import PropTypes from 'prop-types';
import { getIn } from 'formik';

import Asterisk from '../Asterisk';
import FieldError from '../FieldError';

const TextInput = ({
  name,
  label,
  type = 'text',
  required,
  values,
  value,
  errors,
  touched,
  className = '',
  inputClassName = '',
  disabled = false,
  readOnly = false,
  onChange = () => { },
  onBlur = () => { },
  placeholder,
  buttonGroup = '',
  isButtonEnd = false,
  size,
  labelDescription,
  min = null,
  step = null,
  popover = null,
  inline = false,
  autoComplete = 'no',
  labelWidth = 6,
  inputWidth = 6,
  additionalContent = null,
}) => {
  const fieldValue = value !== undefined
    ? value
    : (values ? getIn(values, name) : undefined);

  let sizeClass = '';
  if (size === 'lg') {
    sizeClass = 'form-control-lg';
  } else if (size === 'sm') {
    sizeClass = 'form-control-sm';
  }

  let inputContent = (
    <input
      type={type}
      name={name}
      min={min !== null ? min : undefined}
      step={step !== null ? step : undefined}
      value={fieldValue ?? ''}
      className={`form-control ${sizeClass} ${inputClassName}`}
      disabled={disabled}
      onChange={onChange}
      onBlur={onBlur}
      autoComplete={autoComplete}
      placeholder={placeholder}
      aria-label={name}
    />
  );

  if (buttonGroup) {
    inputContent = (
      <div className='input-group'>
        {!isButtonEnd && <div className='input-group-text' id={name}>{buttonGroup}</div>}
        {inputContent}
        {isButtonEnd && <div className='input-group-text' id={name}>{buttonGroup}</div>}
      </div>
    );
  }

  let containerClassName = `form-group col-12 rounded ${className}`;
  if (!disabled && !readOnly && errors[name] && touched[name]) {
    containerClassName += ' has-danger';
  }
  if (readOnly) {
    containerClassName += ' bg-light-hover read-only';
  }
  if (inline) {
    containerClassName += ' row';
  }

  let labelClassName = 'form-label';
  if (inline) {
    labelClassName += ` col-12 col-sm-${labelWidth}`;
    if (readOnly) {
      labelClassName += ' mb-0';
    }
  } else {
    labelClassName += ' mb-0';
  }

  return (
    <div className={containerClassName}>
      <label htmlFor={name} className={labelClassName}>
        {label}{readOnly && ':'}{required && !readOnly && <Asterisk />}{popover}
      </label>
      {labelDescription && <div className='form-text'>{labelDescription}</div>}
      <div className={`${inline ? `col-12 col-sm-${inputWidth}` : ''}`}>
        {readOnly
          ? <span className='d-block text-truncate'>{values[name]}{additionalContent}</span>
          : (
            <>
              {inputContent}
              <FieldError error={errors[name]} touched={touched[name]} disabled={disabled} />
              {additionalContent}
            </>
          )
        }
      </div>
    </div>
  );
};

TextInput.propTypes = {
  name: PropTypes.string,
  label: PropTypes.string,
  type: PropTypes.string,
  required: PropTypes.bool,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  values: PropTypes.object,
  errors: PropTypes.object,
  touched: PropTypes.object,
  className: PropTypes.string,
  min: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  step: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  inputClassName: PropTypes.string,
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  placeholder: PropTypes.string,
  buttonGroup: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
  isButtonEnd: PropTypes.bool,
  size: PropTypes.string,
  labelDescription: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.element),
    PropTypes.element,
    PropTypes.string,
  ]),
  inline: PropTypes.bool,
  labelWidth: PropTypes.number,
  inputWidth: PropTypes.number,
  autoComplete: PropTypes.string,
  popover: PropTypes.oneOfType([PropTypes.node, PropTypes.instanceOf(null)]),
  additionalContent: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
};

export default TextInput;
